/**
 * CMS Adapters Hook
 * 
 * Fetches and provides CMS adapter data (content types, filters, field mappings)
 * from the DSL adapters API. Used by Visual Builder, Code Editor, and DSL Builder.
 * 
 * @version 1.0.0
 */

import { useQuery } from '@tanstack/react-query';

// Types
export interface CMSContentType {
  label: string;
  description: string;
  table?: string;
  default_orderby?: string;
}

export interface CMSFilter {
  description: string;
  example: string;
  handler?: string;
  cms?: string;
}

export interface CMSFieldMapping {
  column: string;
  type: string;
  sortable?: boolean;
  filterable?: boolean;
}

export interface CMSAdapterInfo {
  name: string;
  types: Record<string, CMSContentType>;
  filters: Record<string, CMSFilter>;
  fields: Record<string, CMSFieldMapping>;
  statuses: Record<string, string | number>;
}

export interface AdaptersResponse {
  success: boolean;
  adapters: Record<string, CMSAdapterInfo>;
  total: number;
  content_types: string[];
  filters: Record<string, CMSFilter>;
}

// CMS Display Configuration
export const CMS_DISPLAY_CONFIG: Record<string, {
  icon: string;
  color: string;
  label: string;
  bgColor: string;
  textColor: string;
}> = {
  wordpress: { 
    icon: '🔵', 
    color: 'blue', 
    label: 'WordPress',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-800'
  },
  joomla: { 
    icon: '🟠', 
    color: 'orange', 
    label: 'Joomla',
    bgColor: 'bg-orange-100',
    textColor: 'text-orange-800'
  },
  drupal: { 
    icon: '🔷', 
    color: 'cyan', 
    label: 'Drupal',
    bgColor: 'bg-cyan-100',
    textColor: 'text-cyan-800'
  },
  native: { 
    icon: '⚪', 
    color: 'gray', 
    label: 'Native',
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-800'
  },
  generic: { 
    icon: '🌐', 
    color: 'purple', 
    label: 'All CMS',
    bgColor: 'bg-purple-100',
    textColor: 'text-purple-800'
  },
};

// CMS-specific terminology mapping
export const CMS_TERMINOLOGY: Record<string, {
  contentType: string;
  contentTypePlural: string;
  excerpt: string;
  author: string;
  date: string;
  category: string;
  tag: string;
}> = {
  wordpress: { 
    contentType: 'post', 
    contentTypePlural: 'posts', 
    excerpt: 'excerpt', 
    author: 'author', 
    date: 'post_date', 
    category: 'category', 
    tag: 'tag' 
  },
  joomla: { 
    contentType: 'article', 
    contentTypePlural: 'articles', 
    excerpt: 'introtext', 
    author: 'created_by', 
    date: 'created', 
    category: 'catid', 
    tag: 'tags' 
  },
  drupal: { 
    contentType: 'node', 
    contentTypePlural: 'nodes', 
    excerpt: 'body', 
    author: 'uid', 
    date: 'created', 
    category: 'taxonomy', 
    tag: 'tags' 
  },
  native: { 
    contentType: 'content', 
    contentTypePlural: 'contents', 
    excerpt: 'excerpt', 
    author: 'author', 
    date: 'date', 
    category: 'category', 
    tag: 'tag' 
  },
  generic: { 
    contentType: 'post', 
    contentTypePlural: 'posts', 
    excerpt: 'excerpt', 
    author: 'author', 
    date: 'date', 
    category: 'category', 
    tag: 'tag' 
  },
};

// Common/Universal filters available in all CMS
export const UNIVERSAL_FILTERS: Record<string, CMSFilter> = {
  esc_html: { description: 'Escape HTML entities for safe output', example: '{title | esc_html}' },
  esc_url: { description: 'Escape and validate URL', example: '{link | esc_url}' },
  esc_attr: { description: 'Escape for HTML attribute', example: '{title | esc_attr}' },
  strip_tags: { description: 'Remove all HTML tags', example: '{content | strip_tags}' },
  upper: { description: 'Convert to uppercase', example: '{title | upper}' },
  lower: { description: 'Convert to lowercase', example: '{title | lower}' },
  capitalize: { description: 'Capitalize first letter', example: '{title | capitalize}' },
  truncate: { description: 'Truncate to specified length', example: '{text | truncate:100}' },
  trim: { description: 'Remove leading/trailing whitespace', example: '{text | trim}' },
  date: { description: 'Format date/timestamp', example: '{date | date:format="F j, Y"}' },
  number_format: { description: 'Format number with decimals', example: '{price | number_format:2}' },
  default: { description: 'Provide fallback value if empty', example: '{value | default:"N/A"}' },
  raw: { description: 'Output without escaping (use carefully)', example: '{html | raw}' },
  json: { description: 'JSON encode value', example: '{data | json}' },
  nl2br: { description: 'Convert newlines to <br> tags', example: '{text | nl2br}' },
  slug: { description: 'Convert to URL-friendly slug', example: '{title | slug}' },
  length: { description: 'Get string/array length', example: '{items | length}' },
  first: { description: 'Get first item from array', example: '{items | first}' },
  last: { description: 'Get last item from array', example: '{items | last}' },
  join: { description: 'Join array with separator', example: '{tags | join:", "}' },
  split: { description: 'Split string into array', example: '{text | split:","}' },
  sort: { description: 'Sort array', example: '{items | sort}' },
  reverse: { description: 'Reverse array/string', example: '{items | reverse}' },
  keys: { description: 'Get object keys', example: '{object | keys}' },
  values: { description: 'Get object values', example: '{object | values}' },
};

/**
 * Hook to fetch CMS adapter data
 */
export function useCMSAdapters() {
  return useQuery<AdaptersResponse>({
    queryKey: ['cms-adapters'],
    queryFn: async () => {
      const res = await fetch('/api/v1/dsl/adapters');
      if (!res.ok) throw new Error('Failed to fetch adapters');
      return res.json();
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Get filters for a specific CMS type
 */
export function getFiltersForCMS(
  adapters: AdaptersResponse | undefined, 
  cmsType: string
): Record<string, CMSFilter> {
  if (!adapters?.adapters) {
    return UNIVERSAL_FILTERS;
  }

  const cmsFilters = adapters.adapters[cmsType]?.filters || {};
  
  // Merge universal filters with CMS-specific filters
  return {
    ...UNIVERSAL_FILTERS,
    ...cmsFilters,
  };
}

/**
 * Get all filters grouped by CMS
 */
export function getAllFiltersGrouped(
  adapters: AdaptersResponse | undefined
): Record<string, Record<string, CMSFilter>> {
  const grouped: Record<string, Record<string, CMSFilter>> = {
    universal: UNIVERSAL_FILTERS,
  };

  if (adapters?.adapters) {
    for (const [cmsId, adapter] of Object.entries(adapters.adapters)) {
      if (adapter.filters && Object.keys(adapter.filters).length > 0) {
        grouped[cmsId] = adapter.filters;
      }
    }
  }

  return grouped;
}

/**
 * Get content types for a specific CMS
 */
export function getContentTypesForCMS(
  adapters: AdaptersResponse | undefined,
  cmsType: string
): Record<string, CMSContentType> {
  if (!adapters?.adapters) {
    return {};
  }

  return adapters.adapters[cmsType]?.types || {};
}

/**
 * Get all content types across all CMS
 */
export function getAllContentTypes(
  adapters: AdaptersResponse | undefined
): string[] {
  return adapters?.content_types || [];
}

/**
 * Convert filters to expression suggestions format
 */
export function filtersToSuggestions(
  filters: Record<string, CMSFilter>,
  cmsType?: string
): Array<{
  label: string;
  value: string;
  description: string;
  category: 'filter';
  cms?: string;
}> {
  return Object.entries(filters).map(([name, filter]) => ({
    label: name,
    value: `| ${name}`,
    description: filter.description,
    category: 'filter' as const,
    cms: filter.cms || cmsType,
  }));
}

/**
 * Get terminology for a CMS type
 */
export function getTerminology(cmsType: string) {
  return CMS_TERMINOLOGY[cmsType] || CMS_TERMINOLOGY.generic;
}

/**
 * Get display config for a CMS type
 */
export function getCMSDisplayConfig(cmsType: string) {
  return CMS_DISPLAY_CONFIG[cmsType] || CMS_DISPLAY_CONFIG.generic;
}

export default useCMSAdapters;
